// /e/{id}: an exam as its instructor published it (RFC-048 "The Exams page",
// byj-1wv.56.5), as the class's manage page is laid out (byj-1wv.51.12): a
// bar under the head saying how it stands — the version published, how it
// is sat, what waits to be graded — and the one thing to do next; then
// tabs: the classes it is assigned to, each to its submissions; the exam
// itself — how every question is scored, what the student is given, each
// question with its bar, its check and calibrate's budgets; and its kit —
// the last check on the machine, the hidden part by name, its versions. It
// is edited where it is read: how it is sat from the bar, the scoring from
// its card, each question from its row, each in a dialog saved to the draft
// (components/ExamEditor); while there is a draft, the tabs show it. The
// steps and their commands are behind "How exams work". The exam is
// withdrawn from a class from its row's ⋯, or deleted from the head's,
// asked first (byj-1wv.56.14).
import { useCallback, useEffect, useState } from "react";
import { api, examExportURL, type ClassSummary, type ExamDetail, type ExamDraft, type ExamDraftBody, type ExamMeta, type ExamQuestion } from "../api";
import { CommandLine } from "../components/CommandLine";
import { useConfirm } from "../components/Confirm";
import { DraftStatus, QuestionDialog, ScoringDialog, SettingsDialog, blankDraft, draftFrom } from "../components/ExamEditor";
import { AssignHow, ExamGuideButton, NextStep } from "../components/ExamGuide";
import { BankGuideButton } from "../components/BankGuide";
import { Link } from "../components/Nav";
import { Page, PageHead, PageLoading, PageNotice } from "../components/Page";
import { RowMenu } from "../components/RowMenu";
import { EmptyRows, initialOf } from "../components/Rows";
import { StateBar } from "../components/StateBar";
import { panelId, tabId, Tabs } from "../components/Tabs";
import { barWords, draftNotice, examStage } from "../exams";
import { navigate } from "../router";
import { t, whenMinute, word, phrases } from "../i18n";
import { openClassExams } from "../classManage";
import { Rubric } from "../components/Rubric";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** A question's check as the instructor's machine last ran it, and what calibrate proposed for it. */
export type QuestionCheck = { state: "ok" | "fail" | "none"; calibrate?: string };

/**
 * The checks of each question, for the instructor: the draft's, once the
 * machine has checked the draft as it is now, else validate's of the version
 * published — and calibrate's proposal; a question with neither has no check
 * yet (byj-1wv.51.12).
 */
export function questionChecks(meta: ExamMeta | undefined, draft: ExamDraft | null): Map<string, QuestionCheck> {
  const out = new Map<string, QuestionCheck>();
  const st = draft?.status && draft.status.draft_updated_at === draft.updated_at ? draft.status : null;
  const ran = st ? st.checks.map((c) => ({ id: c.id, ok: c.ok })) : (meta?.instructor?.validation?.checks ?? []).map((c) => ({ id: c.id, ok: c["ok?"] }));
  for (const c of ran) out.set(c.id, { state: c.ok ? "ok" : "fail" });
  for (const q of meta?.instructor?.calibration?.questions ?? []) {
    const calibrate = q.proposed ? t("exams:calibrateProposed", { bar: barWords(q.proposed) }) : q.why ? t("exams:calibrateCouldNot", { why: q.why }) : undefined;
    out.set(q.id, { state: out.get(q.id)?.state ?? "none", calibrate });
  }
  return out;
}

/** The questions, each with its mode, prompt, what it hands in and its bar; for the instructor, its check and calibrate's proposal, and its Edit. */
export function Questions({ questions, onEdit, editing, checks }: {
  questions: ExamQuestion[];
  onEdit?: (i: number) => void;
  /** the one open in its dialog, whose Edit names it, so the dialog gives the focus back to it */
  editing?: number;
  checks?: Map<string, QuestionCheck>;
}) {
  return (
    <ol className="stack exam-questions" data-testid="exam-questions">
      {questions.map((q, i) => (
        <li key={`${q.id}-${i}`} className="stack">
          <div className="row wrap">
            <code>{q.id}</code>
            <span className="chip">{word("exams:questionMode", q.mode) ?? q.mode}</span>
            {q.submit && q.submit.length > 0 && <span className="muted small">{phrases.submitWhat(q.submit)}</span>}
            {checks && (
              <span className={`chip question-check is-${checks.get(q.id)?.state ?? "none"}`} data-testid="question-check">
                {word("exams:questionCheck", checks.get(q.id)?.state ?? "none")}
              </span>
            )}
            {onEdit && (
              <>
                <span className="grow" />
                <button
                  className="small"
                  aria-label={t("exams:editQuestion", { id: q.id })}
                  aria-haspopup="dialog"
                  aria-controls={editing === i ? "exam-question" : undefined}
                  onClick={() => onEdit(i)}
                >
                  {t("common:edit")}
                </button>
              </>
            )}
          </div>
          <p className={`pre-wrap flush${q.prompt.trim() ? "" : " muted"}`}>{q.prompt.trim() || t("exams:editorQuestionUntitled")}</p>
          {q.criteria && barWords(q.criteria) && <p className="muted small flush">{barWords(q.criteria)}</p>}
          {checks?.get(q.id)?.calibrate && <p className="muted small flush calibrate-line">{checks.get(q.id)!.calibrate}</p>}
        </li>
      ))}
    </ol>
  );
}

/**
 * What the student is given, an agent kind a line: its harness's files, or
 * nothing; for the instructor, whether validate found each harness gives the
 * agents named.
 */
function Given({ meta }: { meta: ExamMeta }) {
  const checked = new Map((meta.instructor?.validation?.harness ?? []).map((h) => [h.kind, h["ok?"]]));
  return (
    <section aria-labelledby="exam-given-h" className="card pad exam-given">
      <h3 id="exam-given-h" className="flush small">{t("exams:examHarness")}</h3>
      <ul className="small flush given" data-testid="exam-harness">
        {meta.agents.map((k) => (
          <li key={k}>
            <strong>{k}</strong>
            {checked.has(k) && <span className={checked.get(k) ? "ok-mark" : "error"}>{checked.get(k) ? " ✓" : " ✗"}</span>}{" "}
            {(meta.harness?.[k] ?? []).length === 0 ? (
              <span className="muted">{t("exams:examHarnessNone")}</span>
            ) : (
              (meta.harness?.[k] ?? []).map((f) => (
                <code key={f} className="file-name">
                  {f}
                </code>
              ))
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The hidden part, by name alone, for the instructor: when validate last held, and its files, which stay on their machine, or for a paper of the bank, sealed by the bank. */
function HiddenPart({ ins, managed }: { ins: NonNullable<ExamMeta["instructor"]>; managed: boolean }) {
  return (
    <section aria-labelledby="exam-hidden-h" className="card pad stack" data-testid="exam-instructor">
      <h3 id="exam-hidden-h" className="flush">{t("exams:examHidden")}</h3>
      {ins.validation?.["checked-at"] && <p className="muted small flush">{t("exams:examCheckedAt", { w: whenMinute(new Date(ins.validation["checked-at"]).toISOString()) })}</p>}
      <ul className="small flush hidden-files" data-testid="exam-hidden">
        {(ins.hidden ?? []).map((f) => (
          <li key={f}>
            <code>hidden/{f}</code>
          </li>
        ))}
      </ul>
      <p className="muted small flush">{managed ? t("exams:examHiddenNoteBank") : t("exams:examHiddenNote")}</p>
    </section>
  );
}

type Assigned = ExamDetail["assignments"][number];

/** A class the exam is assigned to: its window, its submissions; the class and Withdraw behind its ⋯. */
function AssignedRow({ a, onWithdraw }: { a: Assigned; onWithdraw: (a: Assigned) => void }) {
  const submissions = `/ea/${encodeURIComponent(a.id)}`;
  return (
    <li className="share-row exam-row is-live">
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        {initialOf(a.class.name)}
      </span>
      <div className="row-main">
        <Link to={submissions} className="row-title">
          {a.class.name}
        </Link>
        <div className="row-sub">
          {t("exams:examVersion", { v: a.version })}
          {a.opens_at && <> · {t("exams:opensAt", { w: whenMinute(a.opens_at) })}</>}
          {a.closes_at && <> · {t("exams:closesAt", { w: whenMinute(a.closes_at) })}</>}
        </div>
      </div>
      <div className="row-meta">
        <Link to={submissions} className="button small">
          {t("exams:submissionsTitle")}
        </Link>
      </div>
      <RowMenu
        label={t("shares:moreFor", { title: a.class.name })}
        items={[
          { label: t("exams:openTheClass"), onSelect: () => openClassExams(a.class.id) },
          { label: t("exams:withdrawExam"), onSelect: () => onWithdraw(a), danger: true },
        ]}
      />
    </li>
  );
}

type ExamTab = "classes" | "questions" | "kit";
const examTabs: ExamTab[] = ["classes", "questions", "kit"];

/** Which part is open in its dialog: how it is sat, the scoring, a question (its index; past the last, a new one). */
type Editing = { part: "settings" } | { part: "scoring" } | { part: "question"; i: number } | null;

export function ExamPage({
  id,
  load = api.exam,
  loadDraft = api.examDraft,
  putDraft = api.putExamDraft,
  withdraw = api.withdrawExam,
  remove = api.deleteExam,
  loadClasses = api.classes,
}: {
  id: string;
  load?: typeof api.exam;
  loadDraft?: typeof api.examDraft;
  putDraft?: typeof api.putExamDraft;
  withdraw?: typeof api.withdrawExam;
  remove?: typeof api.deleteExam;
  loadClasses?: typeof api.classes;
}) {
  const [exam, setExam] = useState<ExamDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  // the exam as edited on this page, and what the instructor's machine made of it (byj-1wv.56.8)
  const [draft, setDraft] = useState<ExamDraft | null>(null);
  const [editing, setEditing] = useState<Editing>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // none picked, it opens on its classes, or on its questions while it is assigned to none
  const [picked, setPicked] = useState<ExamTab | null>(null);
  // the classes the reader teaches, for the commands that assign it to each
  const [classes, setClasses] = useState<ClassSummary[] | null>(null);
  const [confirm, confirmDialog] = useConfirm();
  const reload = useCallback(() => load(id).then(setExam, (err: unknown) => setError(errText(err))), [id, load]);
  useEffect(() => {
    void reload();
    loadDraft(id).then(setDraft, () => setDraft(null));
    loadClasses().then(setClasses, () => setClasses([]));
  }, [id, reload, loadDraft, loadClasses]);
  if (error) {
    return (
      <PageNotice error={error}>
        <Link to="/exams">{t("exams:examsTitle")}</Link>
      </PageNotice>
    );
  }
  if (!exam) return <PageLoading />;
  const withdrawFrom = async (a: Assigned) => {
    if (!(await confirm(t("exams:withdrawExamConfirm", { cls: a.class.name }), { action: t("exams:withdrawExam"), danger: true }))) return;
    try {
      await withdraw(a.id);
      setActionError(null);
    } catch (err) {
      setActionError(errText(err));
    }
    await reload();
  };
  const deleteIt = async () => {
    if (!(await confirm(phrases.deleteExamConfirm(exam.title, exam.assignments.length), { action: t("exams:deleteExam"), danger: true }))) return;
    try {
      await remove(exam.id);
      navigate("/exams");
    } catch (err) {
      setActionError(errText(err));
    }
  };
  const latest = exam.versions[exam.versions.length - 1];
  const meta = latest?.meta;
  const ins = meta?.instructor;
  // what the page shows and edits: the draft, else the version published, else a new exam's first draft
  const body: ExamDraftBody = draft?.body ?? (meta ? draftFrom(meta) : blankDraft(exam.title));
  const published = new Set((meta?.questions ?? []).map((q) => q.id));
  const save = async (next: ExamDraftBody) => setDraft(await putDraft(exam.id, next));
  // a question taken out of the draft, asked first as every removal here is; one refused is said on the page
  const removeQuestion = async (i: number) => {
    const x = body.questions[i]!;
    if (!(await confirm(t("exams:removeQuestionConfirm", { id: x.id }), { action: t("exams:editorRemove"), danger: true }))) return;
    try {
      await save({ ...body, questions: body.questions.filter((_, j) => j !== i) });
      setActionError(null);
    } catch (err) {
      setActionError(errText(err));
    }
    setEditing(null);
  };
  const setQuestion = (i: number, q: ExamQuestion) => save({ ...body, questions: i < body.questions.length ? body.questions.map((x, j) => (j === i ? q : x)) : [...body.questions, q] });
  // the guide's command that assigns it names the class, when it is assigned to one
  const code = exam.assignments.length === 1 ? exam.assignments[0]!.class.join_code : undefined;
  // a paper of the organisation's bank (RFC-056): the bank's to change, never edited here
  const managed = !!exam.bank;
  // a paper of the bank has nothing to edit and no next step above the tabs: with no class, how it is set again is on Classes (byj-1wv.96)
  const tab = picked ?? (exam.assignments.length > 0 || managed ? "classes" : "questions");
  const notice = draftNotice(draft);
  // the one thing to do next (byj-1wv.51.12)
  const stage = examStage(
    { published: !!meta, assigned: exam.assignments.length, queued: exam.queued, written: body.questions.some((x) => x.prompt.trim() !== "") },
    draft,
  );
  const head: Record<ExamTab, number | null> = {
    classes: exam.assignments.length,
    questions: body.questions.length,
    kit: null,
  };
  const checks = questionChecks(meta, draft);
  // a panel keeps the UA's display, so its hidden hides it: what lays it out is inside it
  const panel = (id: ExamTab) => ({ role: "tabpanel", id: panelId("exam", id), "aria-labelledby": tabId("exam", id), hidden: tab !== id });
  // a draft the machine has published as it is shows nothing new: only one still to check or fix is said
  const showingDraft = notice && <p className="muted small flush showing-draft">{t("exams:showingDraft")}</p>;
  const pullCmd = meta ? "byj exam pull KIT --watch --publish" : t("exams:pullNew");
  const q = editing?.part === "question" ? editing.i : -1;
  return (
    <Page className="exams-page">
      {confirmDialog}
      <PageHead
        title={exam.title}
        long
        actions={
          <>
            {/* a paper of the bank is not written, checked or published here: the bank's guide says what is done with one (byj-1wv.96) */}
            {managed ? <BankGuideButton /> : <ExamGuideButton code={code} />}
            <RowMenu label={t("shares:moreFor", { title: exam.title })} items={[{ label: t("exams:deleteExam"), onSelect: () => void deleteIt(), danger: true }]} />
          </>
        }
      />
      <StateBar
        className="exam-bar"
        label={t("exams:examTabs")}
        state={meta ? "live" : "paused"}
        stateLabel={<span className="share-bar-state">{latest ? t("exams:examPublishedState", { v: latest.version }) : t("exams:examNotPublished")}</span>}
        actions={
          <>
            {exam.queued > 0 && <span className="chip chip-warn">{t("exams:toGrade", { n: exam.queued })}</span>}
            {managed && <span className="chip" data-testid="exam-from-bank">{t("exams:fromBank")}</span>}
            {/* a managed exam's edits are not drawn at all: a button's display outranks the browser's own [hidden] (byj-1wv.94) */}
            {!managed && (
              <button
                className="small"
                aria-label={t("exams:editSettings")}
                aria-haspopup="dialog"
                aria-controls={editing?.part === "settings" ? "exam-settings" : undefined}
                onClick={() => setEditing({ part: "settings" })}
              >
                {t("common:edit")}
              </button>
            )}
          </>
        }
        testid="exam-bar"
      >
        <span className="share-bar-facts" data-testid="exam-settings">
          {body.agents.join(", ")}
          {body.time_limit_min ? ` · ${t("exams:examTimeLimit", { m: body.time_limit_min })}` : ""} · {t("exams:examAttemptsAllowed", { count: body.attempts ?? 1 })}
        </span>
        {meta && <code className="share-bar-facts">{meta.id}</code>}
      </StateBar>
      {managed && <p className="muted small" data-testid="exam-bank-lead">{t("exams:fromBankLead")}</p>}
      {stage && !managed && (
        // never published, the kit is made and checked by byj's exam-kit agent, which publishes only when asked: its own command does
        <NextStep stage={stage} text={stage === "check" && !meta ? t("exams:stageCheckNew") : undefined}>
          {(stage === "check" || stage === "fix") && (
            <>
              {draft && <p className="muted small flush">{t("exams:draftLine", { when: whenMinute(draft.updated_at) })}{notice && <> — {word("exams:draftNotice", notice)}</>}</p>}
              {stage === "fix" && (
                <ul className="flush small" data-testid="next-step-problems">
                  {(draft?.status?.problems ?? []).map((p) => (
                    <li key={p} className="error">
                      {p}
                    </li>
                  ))}
                </ul>
              )}
              <p className="muted small flush">{t("exams:kitWhat")}</p>
              <CommandLine command={pullCmd} />
              {!meta && <CommandLine command={t("exams:publishKit")} />}
            </>
          )}
          {stage === "assign" && <AssignHow classes={classes} />}
          {stage === "grade" && (
            <>
              <CommandLine command="byj exam grade KIT --relay" />
              {exam.assignments.length === 1 ? (
                <Link to={`/ea/${encodeURIComponent(exam.assignments[0]!.id)}`} className="button small next-step-go">
                  {t("exams:openSubmissions")}
                </Link>
              ) : (
                <button className="small next-step-go" onClick={() => setPicked("classes")}>
                  {t("exams:examTab.classes")}
                </button>
              )}
            </>
          )}
        </NextStep>
      )}
      {actionError && <p className="error">{t("common:error", { m: actionError })}</p>}

      <Tabs
        prefix="exam"
        label={t("exams:examTabs")}
        value={tab}
        onChange={setPicked}
        tabs={examTabs.map((id) => ({
          id,
          label: (
            <>
              {word("exams:examTab", id)}
              {head[id] !== null && <span className="tab-count">{head[id]}</span>}
            </>
          ),
        }))}
      />

      <div {...panel("classes")} className="rows-panel">
        {exam.assignments.length === 0 ? (
          // assigned from byj alone: how, with the reader's classes' codes; a paper of the bank as it is, to a class it was drawn for (byj-1wv.96)
          <div className="empty-card" data-testid="no-exam-assignments">
            <p className="muted small">
              {!managed ? t("exams:examNotAssigned") : latest?.uploaded ? t("exams:examNotAssignedBank") : t("exams:paperDeliveryCut")}
            </p>
            {managed ? (
              latest?.uploaded ? (
                <AssignHow classes={classes} paper={{ exam: exam.id, version: latest.version, allowed: exam.allowed_classes ?? [] }} />
              ) : (
                // a delivery cut short leaves no version, or one whose kit never arrived, which the relay assigns to no
                // class ("no kit uploaded"): the bank is asked for another
                <p className="muted small flush">
                  <Link to="/bank/request">{t("exams:paperAskAgain")}</Link>
                </p>
              )
            ) : meta ? (
              <AssignHow classes={classes} />
            ) : (
              <p className="muted small">{t("exams:stageText.check")}</p>
            )}
          </div>
        ) : (
          <>
            <div className="rows-head-line">
              <span className="grow" />
              <a className="button small" href={examExportURL(exam.id)} download>
                {t("exams:exportGrades")}
              </a>
            </div>
            <ul className="rows" data-testid="exam-assignments">
              {exam.assignments.map((a) => (
                <AssignedRow key={a.id} a={a} onWithdraw={(x) => void withdrawFrom(x)} />
              ))}
            </ul>
          </>
        )}
      </div>

      {/* the exam itself: how every question is scored, what the student is given, then each question with its check and calibrate's proposal */}
      <div {...panel("questions")}>
        <div className="stack">
          {showingDraft}
          <section aria-labelledby="exam-rubric-h" className="card pad stack">
            <div className="row">
              <h3 id="exam-rubric-h" className="flush grow">{t("exams:examRubric")}</h3>
              {!managed && (
                <button
                  className="small"
                  aria-label={t("exams:editScoring")}
                  aria-haspopup="dialog"
                  aria-controls={editing?.part === "scoring" ? "exam-scoring" : undefined}
                  onClick={() => setEditing({ part: "scoring" })}
                >
                  {t("common:edit")}
                </button>
              )}
            </div>
            {body.rubric && <Rubric rubric={body.rubric} />}
          </section>
          {meta && <Given meta={meta} />}
          <div className="rows-head-line">
            <h3 className="rows-head">
              {t("exams:examQuestions")} · {body.questions.length}
            </h3>
            {!managed && (
              <button
                className="small"
                aria-haspopup="dialog"
                aria-controls={q === body.questions.length ? "exam-question" : undefined}
                onClick={() => setEditing({ part: "question", i: body.questions.length })}
              >
                {t("exams:editorAddQuestion")}
              </button>
            )}
          </div>
          {body.questions.length === 0 ? (
            <EmptyRows text={t("exams:noQuestionsYet")} />
          ) : (
            <section aria-label={t("exams:examQuestions")} className="card pad stack">
              {/* a paper of the bank was validated by the bank before it was delivered: no check of the machine's to say, done or not (byj-1wv.96) */}
              <Questions questions={body.questions} checks={managed ? undefined : checks} editing={q} onEdit={managed ? undefined : (i) => setEditing({ part: "question", i })} />
            </section>
          )}
        </div>
      </div>

      {/* the kit, top to bottom: the last check on the machine (for a paper of the bank, that its versions are the bank's), the hidden part by name, the versions published */}
      <div {...panel("kit")}>
        <div className="stack">
          {managed ? (
            // a paper of the bank: its versions are the bank's, and the relay refuses a draft, its check or a version from the machine (byj-1wv.95)
            <section aria-labelledby="exam-draft-h" className="card pad stack" data-testid="exam-kit-bank">
              <h3 id="exam-draft-h" className="flush">{t("exams:kitFromBankHead")}</h3>
              <p className="muted small flush">{t("exams:kitFromBankNote")}</p>
            </section>
          ) : (
            <section aria-labelledby="exam-draft-h" className="card pad stack">
              <h3 id="exam-draft-h" className="flush">{t("exams:kitLastCheck")}</h3>
              <p className="muted small flush">{t("exams:examEditNote")}</p>
              {draft ? <DraftStatus draft={draft} /> : <p className="muted small flush">{t("exams:draftNone")}</p>}
              <span className="small muted">{t("exams:draftCheckLead")}</span>
              <CommandLine command={pullCmd} />
            </section>
          )}
          {ins && <HiddenPart ins={ins} managed={managed} />}
          <section aria-labelledby="exam-versions-h" className="stack">
            <h3 id="exam-versions-h" className="rows-head flush">
              {t("exams:kitVersions")} · {exam.versions.length}
            </h3>
            {exam.versions.length === 0 ? (
              <EmptyRows text={t("exams:examNotPublished")} />
            ) : (
              <ul className="rows exam-versions" data-testid="exam-versions">
                {exam.versions.map((v) => (
                  <li key={v.version} className="share-row exam-row is-ended no-menu">
                    <span className="row-lead" />
                    <span className="kind-box" aria-hidden="true">
                      v{v.version}
                    </span>
                    <div className="row-main">
                      <span className="row-title">{t("exams:examVersion", { v: v.version })}</span>
                      <div className="row-sub">
                        <code>{v.public_hash.slice(0, 12)}</code> · {whenMinute(v.created_at)}
                      </div>
                    </div>
                    <div className="row-meta">{!v.uploaded && <span className="chip chip-warn">{t("recordings:recordingNotUploaded")}</span>}</div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>

      {editing?.part === "settings" && <SettingsDialog body={body} onSave={save} onClose={() => setEditing(null)} />}
      {editing?.part === "scoring" && <ScoringDialog body={body} onSave={save} onClose={() => setEditing(null)} />}
      {editing?.part === "question" && (
        <QuestionDialog
          q={body.questions[q] ?? { id: `q${body.questions.length + 1}`, mode: "vibe", prompt: "", submit: ["answer.txt"] }}
          isNew={!published.has(body.questions[q]?.id ?? "")}
          taken={body.questions.filter((_, j) => j !== q).map((x) => x.id)}
          onSave={(x) => setQuestion(q, x)}
          onRemove={q < body.questions.length ? () => void removeQuestion(q) : undefined}
          onClose={() => setEditing(null)}
        />
      )}
    </Page>
  );
}
